<template>
  <div class="el-stripe">
    <!-- Gateway Mundipagg -->
    <BusinessModel
      :title="$t('settings.gateways')"
      :callback="updateBusinessModel"
      :loading="accountLoading"
      :hideEditButton="
        !permissionIsEnabled('settings.account.business.model:edit')
      "
      @cancelEdit="cancelBusinessModelUpdate"
      v-if="permissionIsEnabled('settings.account.business.model:edit')"
    >
      <template slot="content">
        <div class="emd-panel__section">
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('account.settings.mundipagg.config') }}
              </h2>
              <p class="emd-text _size_small _color_nonessential">
                {{ $t('account.settings.gateways.disclaimer') }}
              </p>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <div class="emd-form-group">
                <label class="emd-label">{{
                  $t('account.settings.gateways.label')
                }}</label>
                <div class="emd-select">
                  <select v-model="businessModel">
                    <option :value="businessModelEnum.PSP">PSP</option>
                    <option :value="businessModelEnum.GATEWAY">Gateway</option>
                    <option :value="businessModelEnum.HYBRID">
                      {{ $t('account.settings.gateways.options.hybrid') }}
                    </option>
                    <option :value="businessModelEnum.PSP_PHYS">
                      {{ $t('account.settings.gateways.options.psp_phys') }}
                    </option>
                  </select>
                  <div class="emd-select__arrow"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </BusinessModel>
    <mundipagg
      id="mundipagg-test"
      :title="$t('account.settings.mundipagg.title')"
      :callback="saveMundipaggSettings"
      :loading="accountLoading"
      @cancelEdit="resetObject(mundipagg, gatewaySettings.mundipagg)"
      :hideEditButton="mundipaggSettingsNotEditable"
      v-if="permissionIsEnabled('settings.gateways.mundipagg:view')"
    >
      <!-- test account -->
      <template
        slot="content"
        v-if="isTestAccount"
      >
        <div class="emd-panel__section">
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('account.settings.mundipagg.config') }}
              </h2>
            </div>
            <div class="el-chunk">
              <p class="emd-text _color_nonessential _size_small">
                {{
                  $t('account.settings.mundipagg.merchant-key-test-description')
                }}
              </p>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <div class="emd-form-group">
                <label
                  class="emd-label"
                  for="publicMerchantKeyTest"
                  >{{
                    $t('account.settings.mundipagg.public-merchant-key-test')
                  }}</label
                >
                <input
                  class="emd-input fs-hide"
                  id="publicMerchantKeyTest"
                  type="text"
                  v-model="mundipagg.public_merchant_key"
                  v-bind:class="{
                    _state_error:
                      $v.mundipagg.public_merchant_key.$invalid &&
                      $v.mundipagg.public_merchant_key.$dirty
                  }"
                  required
                  @input="$v.mundipagg.public_merchant_key.$touch"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template
        slot="content"
        v-else
      >
        <div class="emd-panel__section">
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('account.settings.mundipagg.config') }}
              </h2>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <div class="emd-form-group">
                <label
                  class="emd-label"
                  for="pmkMundipagg"
                  >{{
                    $t('account.settings.mundipagg.public-merchant-key')
                  }}</label
                >
                <input
                  class="emd-input fs-hide"
                  id="pmkMundipagg"
                  type="text"
                  v-model="mundipagg.public_merchant_key"
                  v-bind:class="{
                    _state_error:
                      $v.mundipagg.public_merchant_key.$invalid &&
                      $v.mundipagg.public_merchant_key.$dirty
                  }"
                  required
                  @input="$v.mundipagg.public_merchant_key.$touch"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </mundipagg>

    <!-- Gateway Pagar.me -->
    <pagarme
      id="pagarme"
      :title="$t('account.settings.pagarme.title')"
      :callback="savePagarmeSettings"
      :loading="accountLoading"
      @cancelEdit="resetObject(pagarme, gatewaySettings.pagarme)"
      v-if="permissionIsEnabled('settings.gateways.pagarme:view')"
      :hideEditButton="pagarmeSettingsNotEditable"
      hasConfirm
      :messageConfirm="$t('account.settings.pagarme.confirm-message')"
    >
      <template slot="content">
        <div class="emd-panel__section">
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('account.settings.pagarme.config') }}
              </h2>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <div class="emd-form-group">
                <label
                  class="emd-label"
                  for="companyId"
                  >{{ $t('account.settings.pagarme.company-id') }}</label
                >
                <input
                  class="emd-input fs-hide"
                  id="companyId"
                  type="text"
                  v-model="pagarme.company_id"
                  @input="$v.pagarme.company_id.$touch"
                  :disabled="hasCompanyId"
                  autocomplete="off"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </pagarme>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ConfigBox from '@/components/project/molecules/ConfigBox.vue'
import { required } from 'vuelidate/lib/validators'
import { BUSINESS_MODEL_ENUM } from '@/enums/business-model.enum.js'

export default {
  name: 'Gateways',
  components: {
    Pagarme: ConfigBox,
    Mundipagg: ConfigBox,
    BusinessModel: ConfigBox
  },
  computed: {
    gatewaySettings: {
      get () {
        return this.$store.getters.gatewaySettings
      }
    },
    accountLoading () {
      return this.$store.getters.accountLoading
    },
    params: function () {
      return {
        merchantId: this.$store.getters.merchantId,
        accountId: this.$store.getters.accountId
      }
    },
    isTestAccount () {
      return this.$store.getters.account.account_type.value === 'test'
    },
    hasCompanyId () {
      return (
        this.gatewaySettings.pagarme &&
        !!this.gatewaySettings.pagarme.company_id
      )
    },
    mundipaggSettingsNotEditable () {
      return (
        this.isTestAccount ||
        !this.permissionIsEnabled('settings.gateways.mundipagg:edit')
      )
    },
    pagarmeSettingsNotEditable () {
      if (!this.permissionIsEnabled('settings.gateways.pagarme:edit'))
        return true
      return this.hasCompanyId
    },
    activeBusinessModel: {
      get () {
        return this.$store.getters.businessModel
      }
    }
  },
  data () {
    return {
      mundipagg: {
        public_merchant_key: null
      },
      pagarme: {
        company_id: null
      },
      businessModel: this.$store.getters.businessModel,
      businessModelEnum: BUSINESS_MODEL_ENUM
    }
  },
  validations: {
    mundipagg: {
      public_merchant_key: {
        required
      }
    },
    pagarme: {
      company_id: {}
    }
  },
  methods: {
    ...mapActions({
      updateMundipaggSettings: 'UPDATE_MUNDIPAGG_SETTINGS',
      updatePagarmeSettings: 'UPDATE_PAGARME_SETTINGS'
    }),
    saveMundipaggSettings (password) {
      this.mundipagg.password = password
      return this.updateMundipaggSettings(this.mundipagg)
    },
    savePagarmeSettings (password) {
      this.pagarme.password = password
      return this.updatePagarmeSettings(this.pagarme)
    },
    resetObject (obj, objFromApi) {
      obj = Object.assign(obj, objFromApi)
    },
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    },
    cancelBusinessModelUpdate () {
      this.businessModel = this.activeBusinessModel
    },
    updateBusinessModel (password) {
      if (this.$store.getters.hasCompanyIdInPagarmeSettings) {
        this.pagarme.password = password
      } else {
        this.mundipagg.password = password
      }
      const body = {
        business_model: this.businessModel,
        password: password
      }
      return this.$store.dispatch('UPDATE_BUSINESS_MODEL', body).finally(() => {
        this.businessModel = this.activeBusinessModel
      })
    }
  },
  created () {
    this.$store.dispatch('GET_ACCOUNT', this.params).then(res => {
      this.mundipagg = Object.assign(
        this.mundipagg,
        this.gatewaySettings.mundipagg
      )
      this.pagarme = Object.assign(this.pagarme, this.gatewaySettings.pagarme)
    })
  }
}
</script>
